import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';

import {
  CampaignDevice, DashboardType,
  IAggregatedStats,
  ICampaignOfferTrend,
  ICampaignOrOffer,
  IChartPoint, IGrossChartPoint,
  IGrossRow,
  IIncrementalRow
} from '../types/global';
import { IProfileOrganization } from '../types/users';
import { makeFilterId } from '../utils/filters';

import { API_URL } from './config';

type RawCampaignTableRow = {
  offer?: string;
  keywords: string[];
  campaign: string;
  domain: number;
  device?: CampaignDevice;
  starts: string;
  ends: string;
  products: ('cc' | 'cs' | 'rules')[];
  offerAttractiveness: number;
  targetedVisits: number;
  carts: number;
  cartCreationRate: number;
  purchases: number;
  purchaseRate: number;
  aov: number;
  offers: number;
  shoppers: number;
  visits: number;
  campaignstartdate: string;
  campaignenddate: string;
  // engagement_classification: string;
  // engagement_trigger_type: string;
  // engagement_is_financial: string;
  // engagement_presentation_type: string;
};

interface IRawIncrementalRow extends RawCampaignTableRow {
  testGroup: 'control' | 'treatment';
  incrementalRevenue: number;
  conversionChange: number;
  cartCreationChange: number;
  cartCreationRateDiff: number;
  purchaseRateDiff: number;
}

interface IRawGrossRow extends RawCampaignTableRow {}

export const toPercentage = <T extends number | undefined>(x: T) => typeof x === 'number' ? x * 100 : x;

const getDomainName = (organizations: IProfileOrganization[], domainId: number) => (
  organizations.flatMap((x) => x.domains)
    .find((x) => x.id === domainId)!.name
);

export interface IAnnotation {
  annotation: string; // id
  annotation_text: string;
  domain: number;
  campaign: string;
  offer: string;
  created_at: string;
  updated_at?: string;
}

type RawRow = IRawIncrementalRow | IRawGrossRow;
type WithAnnotation = RawRow & {
  annotationId?: string;
  annotationText?: string;
  annotationDate?: string;
};
type RawRowWithPath = WithAnnotation & { path: string[] };

export interface IRawKPIResponse {
  table: RawRow[];
  annotations: IAnnotation[];
}

export class DataError extends Error {}

type GetKPIOptions = { domain: number, startDate: string, endDate: string } | { filter: ICampaignOrOffer[] };
type GetKPI = {
  (
    options: GetKPIOptions & { type: 'incremental' },
    organizations: IProfileOrganization[],
    signal?: AbortController['signal'],
  ): Promise<AxiosResponse<IIncrementalRow[]>>;
  (
    options: GetKPIOptions & { type: 'gross' },
    organizations: IProfileOrganization[],
    signal?: AbortController['signal'],
  ): Promise<AxiosResponse<IGrossRow[]>>;
}
export const getKPI: GetKPI = async (
  options: GetKPIOptions & { type: 'incremental' | 'gross' } & { noCache?: boolean },
  organizations: IProfileOrganization[],
  signal?: AbortController['signal'],
): Promise<AxiosResponse<any>> => {
  const { noCache, ...restOptions } = options;
  const params: Record<string, any> = 'filter' in restOptions ? {
    filter: JSON.stringify(restOptions.filter),
  } : {
    domain: restOptions.domain,
    start: restOptions.startDate,
    end: restOptions.endDate,
  };
  if (noCache) params['no-cache'] = 1;
  const response: AxiosResponse<IRawKPIResponse> = await axios.get(`${API_URL}/kpi/tableView`, {
    params,
    signal,
  });
  const { data: { table, annotations } } = response;
  // First, deduplicate the raw table data to prevent duplicates from the backend
  const uniqueTableData = table.filter((item, index, self) => 
    index === self.findIndex(t => 
      t.campaign === item.campaign &&
      t.domain === item.domain &&
      t.offer === item.offer &&
      t.device === item.device &&
      t.campaignstartdate === item.campaignstartdate &&
      t.campaignenddate === item.campaignenddate &&
      t.starts === item.starts &&
      t.ends === item.ends &&
      ('testGroup' in t ? t.testGroup === (item as any).testGroup : true)
    )
  );
  

  const withAnnotations: WithAnnotation[] = uniqueTableData.map((x) => {
    const annotation = annotations.find((y) => (
      y.domain === x.domain
      && y.campaign === x.campaign
      // eslint-disable-next-line eqeqeq
      && y.offer == x.offer
    ));
    const hasEngagementAnnotationsOnly = !annotation && !x.offer && annotations.some((y) => (
      y.domain === x.domain
      && y.campaign === x.campaign
      && y.offer
    ));
    return {
      ...x,
      ...(annotation ? {
        annotationId: annotation.annotation,
        annotationText: annotation.annotation_text,
        annotationDate: annotation.updated_at ?? annotation.created_at,
      } : {}),
      hasEngagementAnnotationsOnly,
    };
  });
  const withDomains: WithAnnotation[] = _.flatMap(
    _.map(
      _.groupBy(withAnnotations, (x) => x.campaign),
      (x) => _.groupBy(x, (y) => y.domain),
    ),
    (domains) => _.flatMap(domains, (rows) => (
      rows.map((x) => Object.values(domains).length > 1 ? {
        ...x,
        campaign: `${x.campaign} / ${getDomainName(organizations, x.domain)}`,
      } : x)
    )),
  );
  const withPathAndPercentage: RawRowWithPath[] = withDomains.map((x) => ({
    ...(restOptions.type === 'incremental' ? x : _.omit(x, 'incrementalRevenue')),
    path: [
      `${x.campaign}|${x.campaignstartdate}|${x.campaignenddate}`,
      ...(x.offer ? [x.offer] : []),
      ...(x.device ? [x.device] : []),
    ],
    offerAttractiveness: toPercentage(x.offerAttractiveness),
    targetedVisits: toPercentage(x.targetedVisits),
    cartCreationRate: toPercentage(x.cartCreationRate),
    purchaseRate: toPercentage(x.purchaseRate),
    ...('testGroup' in x ? {
      cartCreationRateDiff: toPercentage(x.cartCreationRateDiff),
      cartCreationChange: toPercentage(x.cartCreationChange),
      purchaseRateDiff: toPercentage(x.purchaseRateDiff),
      conversionChange: toPercentage(x.conversionChange),
    } : {}),
  }));
  
  // Comprehensive deduplication strategy
  const createUniqueKey = (x: RawRowWithPath) => {
    const baseFields = [
      x.campaign,
      x.domain,
      x.campaignstartdate,
      x.campaignenddate,
      x.starts,
      x.ends,
      x.offer || '',
      x.device || '',
      'testGroup' in x ? x.testGroup : null,
      x.visits,
      x.offers,
      x.carts,
      x.purchases,
      x.aov,
      x.cartCreationRate,
      x.purchaseRate,
      x.targetedVisits,
      x.offerAttractiveness,
    ];
    
    // Add incremental-specific fields if they exist
    if ('testGroup' in x) {
      baseFields.push(
        (x as any).incrementalRevenue || 0,
        (x as any).conversionChange || 0,
        (x as any).cartCreationChange || 0,
        (x as any).cartCreationRateDiff || 0,
        (x as any).purchaseRateDiff || 0
      );
    }
    
    return baseFields.join('|');
  };

  // Remove duplicates by keeping only the first occurrence of each unique key
  const seenKeys = new Set<string>();
  const deduplicatedData: RawRowWithPath[] = [];
  
  withPathAndPercentage.forEach((row) => {
    const uniqueKey = createUniqueKey(row);
    if (!seenKeys.has(uniqueKey)) {
      seenKeys.add(uniqueKey);
      deduplicatedData.push(row);
    }
  });
  
  const processedData = deduplicatedData;
  
  // Final verification - ensure no duplicates remain
  const finalUniqueKeys = new Set<string>();
  const finalDeduplicatedData: RawRowWithPath[] = [];
  
  processedData.forEach((row) => {
    const uniqueKey = createUniqueKey(row);
    if (!finalUniqueKeys.has(uniqueKey)) {
      finalUniqueKeys.add(uniqueKey);
      finalDeduplicatedData.push(row);
    }
  });
  
  const finalProcessedData = finalDeduplicatedData;
  
  
  const withCreationRemoved: RawRowWithPath[] = finalProcessedData.map((x) => ({
    ...x,
    ...((x.products.includes('cs') || x.cartCreationRate > 90) ? {
      cartCreationRate: undefined,
      cartCreationRateDiff: undefined,
      cartCreationChange: undefined,
    } : {}),
  }));
  const withRevenueFields: (
    RawRowWithPath & { grossRevenue: number, revenuePerVisit: number, revenuePerShopper: number, redemptionRate?: number }
  )[] = withCreationRemoved.map((x) => {
    const grossRevenue = x.aov * x.purchases;
    // Calculate redemptionRate using the provided mapping
    // Use x.offers for engagements and x.offerAttractiveness for engagementAttractiveness
    let redemptionRate: number | undefined;
    if (typeof x.purchases === 'number' && x.purchases > 0 && typeof x.offers === 'number' && typeof x.offerAttractiveness === 'number') {
      redemptionRate = (x.offers * x.offerAttractiveness) / x.purchases;
    }
    return {
      ...x,
      grossRevenue,
      revenuePerVisit: grossRevenue / x.visits,
      revenuePerShopper: grossRevenue / x.shoppers,
      redemptionRate,
    };
  });
  const rowsIsIncremental = (rows: (IRawIncrementalRow | IRawGrossRow)[]): rows is IRawIncrementalRow[] => restOptions.type === 'incremental';
  if (rowsIsIncremental(withRevenueFields)) {
    const grouped = _.groupBy(withRevenueFields, (x) => x.path.join('___'));
    const newData: IIncrementalRow[] = _.compact(_.map(grouped, (x) => {
      const control = x.find((y) => y.testGroup === 'control');
      const treatment = x.find((y) => y.testGroup === 'treatment');
      const rpvDiff = (control?.revenuePerVisit && treatment?.revenuePerVisit)
        ? treatment.revenuePerVisit - control.revenuePerVisit : undefined;
      const rpvChange = (control?.revenuePerVisit && treatment?.revenuePerVisit)
        ? (treatment.revenuePerVisit / control.revenuePerVisit - 1) * 100 : undefined;
      return {
        ...(treatment || x[0]),
        id: `${makeFilterId()}_${x[0].campaign}_${x[0].domain}_${x[0].offer}`,
        products: x[0].products.map((product) => product === 'rules' ? 'rule' : product),
        testGroup: ['control', 'treatment'],
        targetedVisits: [control?.targetedVisits, treatment?.targetedVisits],
        offers: [control?.offers, treatment?.offers],
        carts: [control?.carts, treatment?.carts],
        cartCreationRate: [control?.cartCreationRate, treatment?.cartCreationRate],
        purchases: [control?.purchases, treatment?.purchases],
        purchaseRate: [control?.purchaseRate, treatment?.purchaseRate],
        aov: [control?.aov, treatment?.aov],
        grossRevenue: [control?.grossRevenue, treatment?.grossRevenue],
        revenuePerVisit: [control?.revenuePerVisit, treatment?.revenuePerVisit],
        visits: [control?.visits, treatment?.visits],
        rpvDiff,
        rpvChange,
      };
    }));
    return {
      ...response,
      data: newData,
    };
  }
  const rowsIsGross = (rows: (IRawIncrementalRow | IRawGrossRow)[]): rows is (IRawGrossRow & { path: string[] })[] => restOptions.type === 'gross';
  if (rowsIsGross(withRevenueFields)) {
    const newData: IGrossRow[] = withRevenueFields.filter((x) =>
       (x as any).testGroup !== 'control'
    ).map((x) => ({
      ...x,
      id: `${makeFilterId()}_${x.campaign}_${x.domain}_${x.offer}`,
      products: x.products.map((product) => product === 'rules' ? 'rule' : product),
    }));
    return {
      ...response,
      data: newData,
    };
  }
  return response;
};

interface IRawChartPoint {
  datePoint: string;
  campaign: string;
  domain: number;
  offer?: string;
  testGroup: 'control' | 'treatment';
  offers: number;
  carts: number;
  purchases: number;
  purchasedValue: number;
  purchaseRate: number;
  aov: number;
  purchaseRateChange: number;
  incrementalRevenue: number;
  cartCreationChange: number;
  visits: number;
  shoppers: number;
  cartCreationRate: number;
  rpvisit: number;
}

export const getChartData = async (
  options: GetKPIOptions & { type: 'incremental' | 'gross' } & { noCache?: boolean },
  organizations: IProfileOrganization[],
  signal?: AbortController['signal'],
): Promise<AxiosResponse<ICampaignOfferTrend[]>> => {
  const { noCache, ...restOptions } = options;
  const params: Record<string, any> = 'filter' in restOptions ? {
    filter: JSON.stringify(restOptions.filter),
  } : {
    domain: restOptions.domain,
    start: restOptions.startDate,
    end: restOptions.endDate,
  };
  if (noCache) params['no-cache'] = 1;
  const response = await axios.get(`${API_URL}/kpi/charts`, {
    params,
    signal,
  });
  const { data }: { data: IRawChartPoint[] } = response;
  const withDomains: IRawChartPoint[] = _.flatMap(
    _.map(
      _.groupBy(data, (x) => x.campaign),
      (x) => _.groupBy(x, (y) => y.domain),
    ),
    (domains) => _.flatMap(domains, (points) => (
      points.map((x) => Object.values(domains).length > 1 ? {
        ...x,
        campaign: `${x.campaign} / ${getDomainName(organizations, x.domain)}`,
      } : x)
    )),
  );
  const withPercentage: IRawChartPoint[] = withDomains.map((x) => ({
    ...x,
    purchaseRate: toPercentage(x.purchaseRate),
    cartCreationChange: toPercentage(x.cartCreationChange),
    cartCreationRate: toPercentage(x.cartCreationRate),
  }));
  if (restOptions.type === 'incremental') {
    const grouped = _.groupBy(withPercentage, (x) => x.offer ? `OFFER___${x.offer}` : x.campaign);
    const newData = _.map(grouped, (x): ICampaignOfferTrend => {
      const groupedPoints = _.groupBy(x, (y) => y.datePoint);
      const points = _.map(groupedPoints, (datePoints): IChartPoint => {
        const control = datePoints.find((y) => y.testGroup === 'control');
        const treatment = datePoints.find((y) => y.testGroup === 'treatment');
        const grossRevenue = (treatment?.aov != null && treatment?.purchases != null)
          ? (treatment?.aov || 0) * (treatment?.purchases || 0)
          : undefined;
        const aovDiff = (control?.aov != null && treatment?.aov != null)
          ? treatment.aov - control.aov
          : undefined;
        // Calculate cartCreationChange if not provided by API
        const cartCreationRate = (offers: number, carts: number) => offers > 0 ? carts / offers : undefined;
        const controlCartCreationRate = cartCreationRate(control?.offers ?? 0, control?.carts ?? 0);
        const treatmentCartCreationRate = cartCreationRate(treatment?.offers ?? 0, treatment?.carts ?? 0);
        const cartCreationChange = treatment?.cartCreationChange ?? (
          (controlCartCreationRate != null && treatmentCartCreationRate != null && controlCartCreationRate !== 0)
            ? ((treatmentCartCreationRate / controlCartCreationRate - 1) * 100)
            : undefined
        );
        // Calculate purchaseRateChange if not provided by API
        const purchaseRateChange = treatment?.purchaseRateChange ?? (
          (control?.purchaseRate != null && treatment?.purchaseRate != null && control?.purchaseRate !== 0)
            ? ((treatment.purchaseRate / control.purchaseRate - 1) * 100)
            : undefined
        );
        return {
          date: datePoints[0].datePoint,
          purchaseRate: [control?.purchaseRate, treatment?.purchaseRate],
          newNetRevenue: treatment?.incrementalRevenue,
          cartCreationChange,
          purchaseRateChange,
          aov: [control?.aov, treatment?.aov],
          aovDiff,
          offers: [control?.offers, treatment?.offers],
          carts: [control?.carts, treatment?.carts],
          purchases: [control?.purchases, treatment?.purchases],
          purchasedValue: [control?.purchasedValue, treatment?.purchasedValue],
          grossRevenue,
          revenuePerVisit: [control?.rpvisit, treatment?.rpvisit],
          visits: [control?.visits, treatment?.visits],
          shoppers: [control?.shoppers, treatment?.shoppers],
        };
      });
      return {
        campaign: x[0].campaign,
        ...(x[0].offer ? { offer: x[0].offer } : {}),
        points,
      };
    });
    return {
      ...response,
      data: newData,
    };
  }
  const grouped = _.groupBy(withPercentage.filter((x) =>
    (x as any).testGroup !== 'control'
  ), (x) => x.offer ? `OFFER___${x.offer}` : x.campaign);
  const newData = _.map(grouped, (x): ICampaignOfferTrend => {
    const points = _.uniqBy(_.map(x, (datePoint): IGrossChartPoint => {
      const grossRevenue = (datePoint.aov != null && datePoint.purchases != null)
        ? (datePoint.aov || 0) * (datePoint.purchases || 0)
        : undefined;
      return {
        date: datePoint.datePoint,
        ...datePoint,
        grossRevenue,
        revenuePerVisit: datePoint.rpvisit,
      };
    }), (y) => y.date);
    return {
      campaign: x[0].campaign,
      ...(x[0].offer ? { offer: x[0].offer } : {}),
      points,
    };
  });
  return {
    ...response,
    data: newData,
  };
}

export const getCampaignsAndOffers = async (organization: number): Promise<AxiosResponse<ICampaignOrOffer[]>> => {
  const response = await axios.get<ICampaignOrOffer[]>(`${API_URL}/kpi/campaigns-and-offers`, { params: { organization } });
  const { data } = response;
  return {
    ...response,
    data: [...data]
      // Temporary workaround for a backend bug
      .filter((x) => x.campaign !== null)
      .sort((a, b) => {
        if (!('offer' in a) && 'offer' in b) {
          return -1;
        }
        if ('offer' in a && !('offer' in b)) {
          return 1;
        }
        if (a.offer && b.offer) {
          return a.offer.localeCompare(b.offer);
        }
        return a.campaign.localeCompare(b.campaign);
      })
  };
};

export const getAggregatedStats = async (domain: number, dashboardType: DashboardType): Promise<AxiosResponse<IAggregatedStats>> => {
  const response = await axios.get(`${API_URL}/kpi/lifetime-aggr-stats`, { params: { domain } });
  const { data } = response;
  return {
    ...response,
    data: dashboardType === DashboardType.Incremental ? {
      incrementalRevenue: data.incrementalRevenue,
    } : {
      grossRevenue: data.grossRevenue,
    } as any,
  };
};